import { useEffect } from 'react';

interface ModifierKeys {
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
}

interface UsePressHotkeyOptions {
  enabled?: boolean;
  allowInInputs?: boolean;
  targetWindow?: Window;
  modifiers?: ModifierKeys;
}

// Events already handled by a usePressHotkey instance. Tracked here rather
// than via `defaultPrevented`: Cornerstone's viewport element is focusable and
// its keydown listener prevents default on every key, so after a click/drag in
// the viewer every keypress would otherwise look "claimed" before any hotkey
// saw it.
const claimedEvents = new WeakSet<KeyboardEvent>();

export function usePressHotkey(
  key: string,
  callback: () => void,
  options: UsePressHotkeyOptions = {}
) {
  const {
    enabled = true,
    allowInInputs = false,
    targetWindow = window,
    modifiers,
  } = options;

  useEffect(() => {
    if (!enabled || !targetWindow) return () => {};

    const isValidTarget = (target: EventTarget | null) => {
      if (allowInInputs) return true;
      const el = target as HTMLElement | null;
      const tag = el?.tagName?.toLowerCase();
      return tag !== 'input' && tag !== 'textarea' && !el?.isContentEditable;
    };

    const checkModifiers = (e: KeyboardEvent): boolean => {
      if (!modifiers) return true;
      if (modifiers.ctrl && !e.ctrlKey) return false;
      if (modifiers.meta && !e.metaKey) return false;
      if (modifiers.shift && !e.shiftKey) return false;
      if (modifiers.alt && !e.altKey) return false;
      return true;
    };

    const handler = (e: KeyboardEvent) => {
      // The same hotkey may be registered by several viewer panes — skip an
      // event another instance already claimed so each keypress runs exactly
      // one handler.
      if (claimedEvents.has(e)) return;
      if (!isValidTarget(e.target)) return;

      // macOS Option+L types "¬", so modified letters also match the key's code.
      const keyMatch = modifiers
        ? e.key.toLowerCase() === key.toLowerCase() ||
          e.code === `Key${key.toUpperCase()}`
        : e.key === key;

      if (!keyMatch) return;
      if (!checkModifiers(e)) return;

      claimedEvents.add(e);
      e.preventDefault();
      callback();
    };

    targetWindow.addEventListener('keydown', handler);
    return () => targetWindow.removeEventListener('keydown', handler);
  }, [key, callback, enabled, allowInInputs, targetWindow, modifiers]);
}
